import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import RegisterUser from './RegisterUser';
import UserManagement from './UserManagement';
import ChangePassword from './ChangePassword';
import UserInfo from './UserInfo';
import LoanViewer from './LoanViewer';
import AccountDisbursed from './AccountDisbursed';
import ArchivedLoanPage from './ArchivedLoanPage';
import LoanExportPage from './LoanExportPage';
import useLoanData from '../hooks/useLoanData';
import useSessionValidation from '../hooks/useSessionValidation';
import LoanLoadStatus from './LoanLoadStatus';

function Dashboard({ user, onLogout }) {
  const [view, setView] = useState(() => localStorage.getItem('currentView') || 'dashboard');
  const loanData = useLoanData(user, 'summary', view === 'dashboard');
  const { loans } = loanData;
  const sessionError = useSessionValidation(user?.id, onLogout);
  const [filterRegion, setFilterRegion] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  useEffect(() => {
    localStorage.setItem('currentView', view);
  }, [view]);

  const filteredLoans = loans.filter(loan => {
    const matchesRegion = filterRegion ? loan.region === filterRegion : true;
    const matchesStatus = filterStatus ? loan.loan_status === filterStatus : true;
    const matchesDateFrom = filterFrom ? loan.disbursed_date >= filterFrom : true;
    const matchesDateTo = filterTo ? loan.disbursed_date <= filterTo : true;
    return matchesRegion && matchesStatus && matchesDateFrom && matchesDateTo;
  });

  const countLoanStatuses = () => {
    const statusCounts = {};
    filteredLoans.forEach(loan => {
      const status = loan.loan_status || 'Unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
    return statusCounts;
  };

  const getStatusBadgeClass = (status) => {
    if (!status) return 'status-badge';
    return `status-badge ${status.toLowerCase().replace(/\s/g, '-').replace(/[^a-zA-Z0-9-]/g, '')}`;
  };

  const renderLoanStatusSummary = () => (
    <div className="loan-status-summary" style={{ marginTop: '20px' }}>
      <h2>Loan Status Summary</h2>
      <LoanLoadStatus {...loanData} />
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div>
          <label>Region:</label>
          <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)}>
            <option value="">All</option>
            {Array.from(new Set(loans.map(l => l.region))).map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Status:</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All</option>
            {Array.from(new Set(loans.map(l => l.loan_status))).map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Disbursed Date From:</label>
          <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
        </div>
        <div>
          <label>To:</label>
          <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} />
        </div>
       <button
  id="clear-filters-btn"
  onClick={() => {
    setFilterRegion('');
    setFilterStatus('');
    setFilterFrom('');
    setFilterTo('');
  }}
  className="btn-clear"
>
  Clear Filters
</button>

      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
        {Object.entries(countLoanStatuses()).map(([status, count]) => (
          <div key={status} style={{
            padding: '10px 20px',
            border: '1px solid #ccc',
            borderRadius: '8px',
            minWidth: '150px',
            textAlign: 'center',
            boxShadow: '2px 2px 5px rgba(0,0,0,0.1)',
            backgroundColor: '#fff'
          }}>
            <div className={getStatusBadgeClass(status)} style={{ marginBottom: '8px', display: 'inline-block' }}>
              {status}
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{count}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (view) {
      case 'dashboard':
        return (
          <>
            <h1>Welcome, {`${user.first_name} ${user.middle_name} ${user.last_name}`.trim()}</h1>
            <p>This is your loan tracker dashboard.</p>
            {renderLoanStatusSummary()}
          </>
        );
      case 'user-registration':
        return <RegisterUser />;
      case 'user-management':
        return <UserManagement currentUser={user} />;
      case 'loan-viewer':
        return <LoanViewer currentUser={user} />;
      case 'account-disbursed':
        return <AccountDisbursed currentUser={user} />;
      case 'loan-archive':
        return <ArchivedLoanPage currentUser={user} />;
      case 'change-password':
        return <ChangePassword user={user} />;
      case 'user-info':
        return <UserInfo user={user} />;
      case 'loan-export':
        return <LoanExportPage currentUser={user} />;
      default:
        return <p>Unknown view</p>;
    }
  };

  return (
    <div className="dashboard-wrapper">
      <Sidebar setView={setView} onLogout={onLogout} currentUser={user} />
      <div className="dashboard-content">
        {sessionError && <p role="alert">{sessionError}</p>}
        {renderContent()}
      </div>
    </div>
  );
}

export default Dashboard;
